/* Aerofield Aero — storefront behaviour (vanilla JS, no dependencies) */
(() => {
  'use strict';

  const theme = window.AeroTheme || {};
  const routes = theme.routes || { root: '/', cart: '/cart', cartAdd: '/cart/add', cartChange: '/cart/change' };
  const strings = theme.strings || {};
  const designMode = Boolean(window.Shopify && window.Shopify.designMode);
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  const $ = (sel, root = document) => root.querySelector(sel);
  const $$ = (sel, root = document) => Array.from(root.querySelectorAll(sel));
  const debounce = (fn, wait = 300) => {
    let t;
    return (...args) => {
      clearTimeout(t);
      t = setTimeout(() => fn(...args), wait);
    };
  };
  const jsonHeaders = { 'Content-Type': 'application/json', Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' };
  const parseHTML = (html) => new DOMParser().parseFromString(html, 'text/html');

  /* ---------- Money ---------- */
  function formatMoney(cents) {
    const amount = Number(cents) || 0;
    if (theme.currency && theme.shopCurrency && theme.currency !== theme.shopCurrency) {
      try {
        return new Intl.NumberFormat(theme.locale || undefined, { style: 'currency', currency: theme.currency }).format(amount / 100);
      } catch (e) { /* fall through to shop format */ }
    }
    const format = theme.moneyFormat || '{{amount}}';
    const placeholder = /\{\{\s*(\w+)\s*\}\}/;
    const delimit = (n, precision, thousands = ',', decimal = '.') => {
      const fixed = (n / 100).toFixed(precision).split('.');
      return fixed[0].replace(/(\d)(?=(\d{3})+(?!\d))/g, `$1${thousands}`) + (fixed[1] ? decimal + fixed[1] : '');
    };
    const match = format.match(placeholder);
    let value;
    switch (match && match[1]) {
      case 'amount_no_decimals': value = delimit(amount, 0); break;
      case 'amount_with_comma_separator': value = delimit(amount, 2, '.', ','); break;
      case 'amount_no_decimals_with_comma_separator': value = delimit(amount, 0, '.', ','); break;
      case 'amount_with_apostrophe_separator': value = delimit(amount, 2, "'", '.'); break;
      case 'amount_no_decimals_with_space_separator': value = delimit(amount, 0, ' ', ''); break;
      case 'amount_with_space_separator': value = delimit(amount, 2, ' ', ','); break;
      case 'amount_with_period_and_space_separator': value = delimit(amount, 2, ' ', '.'); break;
      default: value = delimit(amount, 2);
    }
    return format.replace(placeholder, value);
  }

  /* ---------- Toast ---------- */
  let toastTimer;
  function toast(message) {
    let el = $('.aero-toast');
    if (!el) {
      el = document.createElement('div');
      el.className = 'aero-toast glass-chip';
      el.setAttribute('role', 'status');
      Object.assign(el.style, {
        position: 'fixed', left: '50%', bottom: '24px', zIndex: '400',
        transform: 'translate(-50%, 20px)', opacity: '0', transition: 'opacity .3s, transform .3s'
      });
      document.body.appendChild(el);
    }
    el.textContent = message;
    requestAnimationFrame(() => {
      el.style.opacity = '1';
      el.style.transform = 'translate(-50%, 0)';
    });
    clearTimeout(toastTimer);
    toastTimer = setTimeout(() => {
      el.style.opacity = '0';
      el.style.transform = 'translate(-50%, 20px)';
    }, 3200);
  }

  /* ---------- Drawers (menu, search, cart) ---------- */
  let lastFocus = null;
  function openDrawer(name) {
    const drawer = $(`[data-drawer="${name}"]`);
    if (!drawer) return;
    $$('[data-drawer].is-open').forEach((d) => d !== drawer && closeDrawer(d));
    lastFocus = document.activeElement;
    drawer.classList.add('is-open');
    drawer.setAttribute('aria-hidden', 'false');
    document.documentElement.style.overflow = 'hidden';
    const focusTarget = $('input[type="search"]', drawer) || $('[role="dialog"]', drawer);
    if (focusTarget) setTimeout(() => focusTarget.focus({ preventScroll: true }), 80);
  }
  function closeDrawer(drawer) {
    if (!drawer) return;
    drawer.classList.remove('is-open');
    drawer.setAttribute('aria-hidden', 'true');
    if (!$('[data-drawer].is-open')) document.documentElement.style.overflow = '';
    if (lastFocus && typeof lastFocus.focus === 'function') lastFocus.focus({ preventScroll: true });
  }

  document.addEventListener('click', (event) => {
    const opener = event.target.closest('[data-drawer-open]');
    if (opener) {
      event.preventDefault();
      openDrawer(opener.dataset.drawerOpen);
      return;
    }
    const closer = event.target.closest('[data-drawer-close]');
    if (closer) {
      event.preventDefault();
      closeDrawer(closer.closest('[data-drawer]'));
      return;
    }
    const cartLink = event.target.closest('[data-cart-open]');
    if (cartLink && theme.cartType === 'drawer' && !document.body.classList.contains('template-cart')) {
      event.preventDefault();
      openDrawer('cart');
    }
  });

  /* Modals (size guide) */
  document.addEventListener('click', (event) => {
    const opener = event.target.closest('[data-modal-open]');
    if (opener) {
      const modal = $(`[data-modal="${opener.dataset.modalOpen}"]`);
      if (!modal) return;
      lastFocus = opener;
      modal.classList.add('is-open');
      modal.setAttribute('aria-hidden', 'false');
      document.documentElement.style.overflow = 'hidden';
      const panel = $('[role="dialog"]', modal);
      if (panel) setTimeout(() => panel.focus({ preventScroll: true }), 60);
      return;
    }
    const closer = event.target.closest('[data-modal-close]');
    if (closer) closeModal(closer.closest('[data-modal]'));
  });
  function closeModal(modal) {
    if (!modal) return;
    modal.classList.remove('is-open');
    modal.setAttribute('aria-hidden', 'true');
    if (!$('[data-drawer].is-open')) document.documentElement.style.overflow = '';
    if (lastFocus && typeof lastFocus.focus === 'function') lastFocus.focus({ preventScroll: true });
  }

  document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape') {
      $$('[data-modal].is-open').forEach(closeModal);
      const open = $$('[data-drawer].is-open').pop();
      if (open) closeDrawer(open);
      $$('details[data-facet][open]').forEach((d) => d.removeAttribute('open'));
    }
  });

  /* ---------- Cart ---------- */
  function updateCartCount(count) {
    $$('[data-cart-count]').forEach((bubble) => {
      const previous = bubble.textContent.trim();
      bubble.textContent = count;
      if (!bubble.hasAttribute('data-count-always')) bubble.hidden = Number(count) === 0;
      if (String(count) !== previous && Number(count) > 0) {
        bubble.classList.remove('is-bumped');
        void bubble.offsetWidth;
        bubble.classList.add('is-bumped');
      }
    });
  }

  async function refreshCartDrawer() {
    const drawer = $('[data-cart-drawer]');
    if (!drawer) return;
    const response = await fetch(`${routes.root}?section_id=cart-drawer`, { credentials: 'same-origin' });
    const doc = parseHTML(await response.text());
    const fresh = $('[data-cart-drawer]', doc);
    if (!fresh) return;
    $('.drawer__panel', drawer).innerHTML = $('.drawer__panel', fresh).innerHTML;
    drawer.dataset.count = fresh.dataset.count;
    updateCartCount(fresh.dataset.count);
  }

  async function refreshCartPage() {
    const page = $('[data-cart-page]');
    if (!page) return;
    const wrapper = page.closest('.shopify-section');
    if (!wrapper) return window.location.reload();
    const sectionId = wrapper.id.replace('shopify-section-', '');
    const response = await fetch(`${routes.cart}?section_id=${encodeURIComponent(sectionId)}`, { credentials: 'same-origin' });
    const doc = parseHTML(await response.text());
    const fresh = $('[data-cart-page]', doc);
    if (fresh) {
      page.replaceWith(fresh);
      initReveal(fresh);
    }
  }

  async function changeLine(line, quantity, scope) {
    const container = scope.closest('[data-cart-drawer], [data-cart-page]');
    if (container) container.classList.add('is-busy');
    try {
      const response = await fetch(`${routes.cartChange}`, {
        method: 'POST',
        headers: jsonHeaders,
        credentials: 'same-origin',
        body: JSON.stringify({ line: Number(line), quantity: Math.max(0, Number(quantity) || 0) })
      });
      const data = await response.json();
      if (!response.ok || data.status) toast(data.description || data.message || strings.error);
      await Promise.all([refreshCartDrawer(), refreshCartPage()]);
    } catch (error) {
      toast(strings.error || 'Error');
    } finally {
      const live = $('[data-cart-drawer].is-busy, [data-cart-page].is-busy');
      if (live) live.classList.remove('is-busy');
    }
  }

  const debouncedChange = debounce((input) => changeLine(input.dataset.lineQuantity, input.value, input), 350);

  document.addEventListener('change', (event) => {
    const input = event.target.closest('input[data-line-quantity]');
    if (input) debouncedChange(input);
  });

  document.addEventListener('click', (event) => {
    const remove = event.target.closest('[data-line-remove]');
    if (remove) {
      event.preventDefault();
      changeLine(remove.dataset.lineRemove, 0, remove);
    }
  });

  /* Quantity steppers (product forms + cart lines) */
  document.addEventListener('click', (event) => {
    const step = event.target.closest('[data-qty-step]');
    if (!step) return;
    const wrapper = step.closest('[data-quantity]');
    const input = wrapper && $('input', wrapper);
    if (!input) return;
    const min = Number(input.min || 0);
    const next = Math.max(min, (Number(input.value) || 0) + Number(step.dataset.qtyStep));
    if (String(next) === input.value) return;
    input.value = next;
    input.dispatchEvent(new Event('change', { bubbles: true }));
  });

  /* Add to cart */
  document.addEventListener('submit', async (event) => {
    const form = event.target.closest('form[data-type="add-to-cart-form"]');
    if (!form) return;
    event.preventDefault();

    const button = $('[data-add-button]', form);
    const errorBox = $('[data-form-error]', form);
    if (!button || button.disabled) return;

    button.classList.add('is-loading');
    button.setAttribute('aria-disabled', 'true');
    if (errorBox) errorBox.hidden = true;

    try {
      const response = await fetch(`${routes.cartAdd}`, {
        method: 'POST',
        headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        credentials: 'same-origin',
        body: new FormData(form)
      });
      const data = await response.json();
      if (!response.ok || data.status) {
        if (errorBox) {
          errorBox.textContent = data.description || data.message || strings.error;
          errorBox.hidden = false;
        }
        return;
      }
      if (theme.cartType !== 'drawer') {
        window.location.href = routes.cart;
        return;
      }
      await refreshCartDrawer();
      openDrawer('cart');
    } catch (error) {
      if (errorBox) {
        errorBox.textContent = strings.error || 'Error';
        errorBox.hidden = false;
      }
    } finally {
      button.classList.remove('is-loading');
      button.removeAttribute('aria-disabled');
    }
  });

  /* ---------- Product: gallery (grid on desktop, slider on mobile) ---------- */
  function initGallery(gallery) {
    if (gallery.dataset.ready) return;
    gallery.dataset.ready = 'true';
    const track = $('[data-gallery-track]', gallery);
    if (!track) return;
    const items = () => $$('.pgallery__item', track);
    const dots = $$('[data-thumb]', gallery);
    const isSlider = () => track.scrollWidth > track.clientWidth + 4;

    const setActive = (mediaId) => {
      dots.forEach((dot) => dot.classList.toggle('is-active', dot.dataset.thumb === String(mediaId)));
    };
    const scrollToItem = (item, instant) => {
      track.scrollTo({ left: item.offsetLeft - track.offsetLeft - parseFloat(getComputedStyle(track).paddingLeft || 0), behavior: instant || reduceMotion ? 'auto' : 'smooth' });
    };

    dots.forEach((dot) => dot.addEventListener('click', () => {
      const item = items().find((el) => el.dataset.mediaId === dot.dataset.thumb);
      if (item) scrollToItem(item);
      setActive(dot.dataset.thumb);
    }));

    track.addEventListener('scroll', debounce(() => {
      if (!isSlider()) return;
      const left = track.scrollLeft;
      let closest = items()[0];
      items().forEach((item) => {
        if (Math.abs(item.offsetLeft - track.offsetLeft - left) < Math.abs(closest.offsetLeft - track.offsetLeft - left)) closest = item;
      });
      if (closest) setActive(closest.dataset.mediaId);
    }, 80), { passive: true });

    gallery.goToMedia = (mediaId, instant) => {
      const item = items().find((el) => el.dataset.mediaId === String(mediaId));
      if (!item) return;
      if (isSlider()) {
        scrollToItem(item, instant);
      } else if (item !== items()[0]) {
        track.prepend(item);
      }
      setActive(mediaId);
    };

    const featured = gallery.dataset.featured;
    if (featured && items()[0] && items()[0].dataset.mediaId !== featured) {
      requestAnimationFrame(() => gallery.goToMedia(featured, true));
    }
  }

  /* ---------- Product: variant picker ---------- */
  function initVariantPicker(picker) {
    if (picker.dataset.ready) return;
    picker.dataset.ready = 'true';
    const dataEl = $('[data-variants]', picker);
    if (!dataEl) return;
    const variants = JSON.parse(dataEl.textContent);
    const section = picker.closest('[data-product-section]') || document;
    const form = document.getElementById(picker.dataset.formId);
    const fieldsets = $$('fieldset', picker);

    const selection = () => fieldsets.map((fs) => {
      const checked = $('input:checked', fs);
      return checked ? checked.value : null;
    });
    const findVariant = (options) => variants.find((v) => v.options.every((value, i) => value === options[i]));

    const updateAvailability = (current) => {
      fieldsets.forEach((fs, optionIndex) => {
        $$('input', fs).forEach((input) => {
          const candidate = current.slice();
          candidate[optionIndex] = input.value;
          const match = findVariant(candidate);
          const label = input.nextElementSibling;
          if (label) label.classList.toggle('is-unavailable', !match || !match.available);
        });
      });
    };

    const updatePrice = (variant) => {
      const price = $('[data-product-info] [data-price]', section);
      if (!price) return;
      const current = $('[data-price-current]', price);
      const compare = $('[data-price-compare]', price);
      const badge = $('[data-price-badge]', price);
      const onSale = variant.compare_at_price && variant.compare_at_price > variant.price;
      if (current) current.innerHTML = formatMoney(variant.price);
      if (compare) {
        compare.innerHTML = onSale ? formatMoney(variant.compare_at_price) : '';
        compare.hidden = !onSale;
      }
      if (badge) {
        badge.hidden = !onSale;
        if (onSale) badge.textContent = `-${Math.floor(((variant.compare_at_price - variant.price) * 100) / variant.compare_at_price)}%`;
      }
      price.classList.toggle('price--sale', Boolean(onSale));
      price.classList.toggle('price--sold-out', !variant.available);
    };

    const updateButton = (variant) => {
      if (!form) return;
      const button = $('[data-add-button]', form);
      const label = $('[data-add-label]', form);
      const idInput = $('input[name="id"]', form);
      if (idInput && variant) {
        idInput.value = variant.id;
        idInput.disabled = !variant.available;
      }
      if (button) button.disabled = !variant || !variant.available;
      if (label) {
        if (!variant) label.textContent = strings.unavailable;
        else label.textContent = variant.available ? strings.addToCart : strings.soldOut;
      }
      const errorBox = $('[data-form-error]', form);
      if (errorBox) errorBox.hidden = true;
    };

    picker.addEventListener('change', () => {
      const current = selection();
      current.forEach((value, i) => {
        const label = $(`[data-option-label="${i}"]`, picker);
        if (label) label.textContent = value;
      });
      updateAvailability(current);
      const variant = findVariant(current);
      updateButton(variant);
      if (!variant) return;
      updatePrice(variant);

      const gallery = $('[data-gallery]', section);
      if (gallery && gallery.goToMedia && variant.featured_media) gallery.goToMedia(variant.featured_media.id);

      if (picker.hasAttribute('data-update-url')) {
        const url = new URL(window.location.href);
        url.searchParams.set('variant', variant.id);
        window.history.replaceState({}, '', url.toString());
      }
    });

    updateAvailability(selection());
  }

  /* ---------- Product recommendations ---------- */
  async function initRecommendations(el) {
    if (el.dataset.ready || !el.dataset.url) return;
    el.dataset.ready = 'true';
    try {
      const response = await fetch(el.dataset.url);
      const doc = parseHTML(await response.text());
      const fresh = $('[data-recommendations]', doc);
      if (fresh && fresh.innerHTML.trim()) {
        el.innerHTML = fresh.innerHTML;
        initReveal(el);
      }
    } catch (error) { /* recommendations are optional */ }
  }

  /* ---------- Facets (filters + sort) ---------- */
  function initFacets(form) {
    if (form.dataset.ready) return;
    form.dataset.ready = 'true';
    const submit = () => {
      $$('input, select', form).forEach((field) => {
        if (field.value === '' && field.type !== 'checkbox') field.disabled = true;
      });
      form.submit();
    };
    const debounced = debounce(submit, 600);
    form.addEventListener('change', (event) => {
      if (event.target.type === 'number') debounced();
      else submit();
    });
    $$('details[data-facet]', form).forEach((details) => {
      details.addEventListener('toggle', () => {
        if (!details.open) return;
        $$('details[data-facet][open]', form).forEach((other) => other !== details && other.removeAttribute('open'));
      });
    });
  }
  document.addEventListener('click', (event) => {
    if (event.target.closest('details[data-facet]')) return;
    $$('details[data-facet][open]').forEach((d) => d.removeAttribute('open'));
  });

  /* Localization selects */
  document.addEventListener('change', (event) => {
    const select = event.target.closest('select[data-autosubmit]');
    if (select && select.form) select.form.submit();
  });

  /* Share */
  document.addEventListener('click', async (event) => {
    const share = event.target.closest('[data-share]');
    if (!share) return;
    const data = { title: share.dataset.title, url: share.dataset.url };
    try {
      if (navigator.share) {
        await navigator.share(data);
      } else if (navigator.clipboard) {
        await navigator.clipboard.writeText(data.url);
        const msg = $('[data-share-msg]', share.parentElement);
        if (msg) {
          msg.hidden = false;
          setTimeout(() => { msg.hidden = true; }, 2500);
        }
      }
    } catch (error) { /* user cancelled */ }
  });

  /* ---------- Customer pages ---------- */
  function initAccountForms() {
    const recover = $('#RecoverPassword');
    const login = $('#CustomerLogin');
    if (recover && login) {
      const showRecover = (show) => {
        recover.hidden = !show;
        login.hidden = show;
      };
      if (window.location.hash === '#recover' || $('[data-recover-success]', recover) || $('.form-message--error', recover)) showRecover(true);
      document.addEventListener('click', (event) => {
        const toggle = event.target.closest('[data-toggle-recover]');
        if (!toggle) return;
        event.preventDefault();
        showRecover(recover.hidden);
      });
    }

    if (window.Shopify && window.Shopify.CountryProvinceSelector) {
      $$('[data-address-country]').forEach((select) => {
        const suffix = select.dataset.addressCountry;
        // eslint-disable-next-line no-new
        new window.Shopify.CountryProvinceSelector(`AddressCountry${suffix}`, `AddressProvince${suffix}`, {
          hideElement: `AddressProvinceContainer${suffix}`
        });
      });
    }

    $$('[data-address-delete]').forEach((button) => {
      button.addEventListener('click', () => {
        if (!window.confirm(button.dataset.confirm)) return;
        const form = document.createElement('form');
        form.method = 'post';
        form.action = button.dataset.addressDelete;
        form.innerHTML = '<input type="hidden" name="_method" value="delete">';
        document.body.appendChild(form);
        form.submit();
      });
    });
  }

  /* ---------- Header & announcement ---------- */
  function initHeader() {
    const header = $('[data-header]');
    if (!header) return;
    const onScroll = () => header.classList.toggle('is-scrolled', window.scrollY > 24);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
  }

  function initAnnouncement(root = document) {
    $$('[data-announcement]', root).forEach((bar) => {
      if (bar.dataset.ready) return;
      bar.dataset.ready = 'true';
      const items = $$('.announcement__item', bar);
      if (items.length < 2 || designMode) return;
      let index = 0;
      setInterval(() => {
        items[index].classList.remove('is-active');
        index = (index + 1) % items.length;
        items[index].classList.add('is-active');
      }, 4500);
    });
  }

  /* ---------- Reveal on scroll ---------- */
  let observer;
  function initReveal(root = document) {
    const targets = $$('.reveal:not(.is-visible)', root);
    if (!document.body.classList.contains('has-reveal') || designMode || reduceMotion || !('IntersectionObserver' in window)) {
      targets.forEach((el) => el.classList.add('is-visible'));
      return;
    }
    if (!observer) {
      observer = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          entry.target.classList.add('is-visible');
          observer.unobserve(entry.target);
        });
      }, { rootMargin: '0px 0px -8% 0px', threshold: 0.08 });
    }
    targets.forEach((el) => observer.observe(el));
  }

  /* ---------- Boot ---------- */
  function init(root = document) {
    $$('[data-gallery]', root).forEach(initGallery);
    $$('[data-variant-picker]', root).forEach(initVariantPicker);
    $$('[data-recommendations]', root).forEach(initRecommendations);
    $$('[data-facets-form]', root).forEach(initFacets);
    initAnnouncement(root);
    initReveal(root);
  }

  const boot = () => {
    initHeader();
    initAccountForms();
    init();
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();

  /* Theme editor support */
  document.addEventListener('shopify:section:load', (event) => init(event.target));
  document.addEventListener('shopify:section:select', (event) => {
    if (event.target.id === 'shopify-section-cart-drawer') openDrawer('cart');
  });
  document.addEventListener('shopify:section:deselect', (event) => {
    if (event.target.id === 'shopify-section-cart-drawer') closeDrawer($('[data-drawer="cart"]'));
  });
})();
